// components/ChatWidget.jsx — Chatbot v3
import { useState, useRef, useEffect, useCallback } from "react";
import "./ChatWidget.css";

const API_URL  = process.env.REACT_APP_API_URL || "/api";
const MAX_CHARS = 500;

const WELCOME_MSG = {
  role: "assistant",
  content: "¡Hola! Soy Fito, el asistente de eFit 👋 ¿En qué puedo ayudarte? Puedo orientarte sobre planes, facturación electrónica o cómo empezar tu demo.",
};

const INITIAL_SUGGESTIONS = ["Ver precios", "Cómo funciona", "Crear demo", "¿Qué es el e-CF?"];
const WHATSAPP_NUMBER = "18492074046";
const WHATSAPP_URL    = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola, quiero información sobre eFit")}`;

function isWhatsAppSuggestion(text) {
  return text.toLowerCase().includes("whatsapp");
}

function getOrCreateSessionId() {
  const KEY = "efit_chat_session_id";
  let id = localStorage.getItem(KEY);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(KEY, id); }
  return id;
}

function getAuthToken() {
  return localStorage.getItem("token") || null;
}

// ── Tracking ─────────────────────────────────────────────────────────────────
function trackEvent(event, data = {}) {
  const payload = { event, ...data, timestamp: Date.now() };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  if (window.gtag) window.gtag("event", event, data);
  if (window.__CHATBOT_DEBUG__) {
    console.group(`%c[eFit Debug] ${event}`, "color:#6366f1;font-weight:bold");
    console.table(payload);
    console.groupEnd();
  }
}

// ── Scroll al formulario de demo ──────────────────────────────────────────────
function scrollToForm() {
  const el = document.getElementById("form-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

// ── A/B test CTA ─────────────────────────────────────────────────────────────
// Variante A: "Crear demo gratis" | Variante B: "Probar sistema ahora"
const CTA_VARIANT = Math.random() < 0.5 ? "A" : "B";
const CTA_TEXT    = CTA_VARIANT === "A" ? "🚀 Crear demo gratis" : "⚡ Probar sistema ahora";

export default function ChatWidget() {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([WELCOME_MSG]);
  const [suggestions, setSuggestions] = useState(INITIAL_SUGGESTIONS);
  const [showDemoCta, setShowDemoCta] = useState(false);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const isVisitor = !getAuthToken();

  const sessionId    = useRef(getOrCreateSessionId());
  const lastIntent   = useRef(null);
  const lastObjection = useRef(null);
  const bottomRef    = useRef(null);
  const inputRef     = useRef(null);

  // track: wrapper que auto-enriquece con session_id, variant, isVisitor, intent, objection
  const track = useCallback((event, extra = {}) => {
    trackEvent(event, {
      session_id: sessionId.current,
      intent:     lastIntent.current,
      objection:  lastObjection.current,
      variant:    CTA_VARIANT,
      isVisitor,
      ...extra,
    });
  }, [isVisitor]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open, suggestions]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      track("chatbot_open", { source: "landing" });
    }
  }, [open, track]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSend = useCallback(async (text) => {
    const safeText = text.trim().slice(0, MAX_CHARS);
    if (!safeText || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: safeText }]);
    setInput("");
    setSuggestions([]);   // limpiar sugerencias mientras responde
    setError(null);
    setLoading(true);

    try {
      const headers = { "Content-Type": "application/json" };
      const token = getAuthToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({ message: safeText, session_id: sessionId.current }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error al conectar con el asistente.");

      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      setSuggestions(data.suggestions ?? []);
      setShowDemoCta(data.showDemoCta ?? false);

      lastIntent.current    = data.intent    ?? null;
      lastObjection.current = data.objection ?? null;

      track("intent_detected", { intent: data.intent });
      if (data.objection) {
        track("objection_detected", { objection: data.objection });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loading, track]);

  const handleSend = useCallback(() => doSend(input), [doSend, input]);

  const handleSuggestion = useCallback((text) => doSend(text), [doSend]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const charsLeft = MAX_CHARS - input.length;

  return (
    <div className="chat-widget">
      {open && (
        <div className="chat-window" role="dialog" aria-label="Chat con eFit">

          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-avatar">🤖</div>
            <div className="chat-header-info">
              <div className="chat-header-name">Fito · Asistente eFit</div>
              <div className="chat-header-status">● En línea</div>
            </div>
            <button className="chat-close-btn" onClick={() => setOpen(false)} aria-label="Cerrar">×</button>
          </div>

          {/* Mensajes */}
          <div className="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble ${msg.role}`}>
                {msg.content}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="chat-typing" aria-label="Escribiendo…">
                <span /><span /><span />
              </div>
            )}

            {/* Sugerencias — solo cuando no está cargando */}
            {!loading && suggestions.length > 0 && (
              <div className="chat-suggestions">
                {suggestions.map((s, i) =>
                  isWhatsAppSuggestion(s) ? (
                    <a
                      key={i}
                      className="chat-suggestion-btn chat-suggestion-wa"
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      💬 {s}
                    </a>
                  ) : (
                    <button
                      key={i}
                      className="chat-suggestion-btn"
                      onClick={() => handleSuggestion(s)}
                      disabled={loading}
                    >
                      {s}
                    </button>
                  )
                )}
              </div>
            )}

            {/* CTA demo — visitantes con compra o objeción detectada */}
            {!loading && showDemoCta && (
              <button
                className="chat-demo-cta"
                onClick={() => {
                  track("click_create_demo", { source: "chat_cta" });
                  track("conversion_intent",  { source: "chat_cta" });
                  // Guardar contexto para enriquecer el formulario
                  try {
                    localStorage.setItem("efit_chat_lead_context", JSON.stringify({
                      source:    "chatbot",
                      session_id: sessionId.current,
                      intent:    lastIntent.current,
                      objection: lastObjection.current,
                      variant:   CTA_VARIANT,
                    }));
                  } catch (_) {}
                  setOpen(false);
                  scrollToForm();
                }}
              >
                {CTA_TEXT}
              </button>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Error */}
          {error && <div className="chat-error">⚠ {error}</div>}

          {/* Input */}
          <div className="chat-input-area">
            <div className="chat-input-wrapper">
              <textarea
                ref={inputRef}
                className="chat-input"
                placeholder="Escribe tu pregunta…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                maxLength={MAX_CHARS}
                aria-label="Mensaje"
                disabled={loading}
              />
              {input.length > MAX_CHARS * 0.8 && (
                <span className={`chat-char-count ${charsLeft < 50 ? "warn" : ""}`}>
                  {charsLeft}
                </span>
              )}
            </div>
            <button
              className="chat-send-btn"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              aria-label="Enviar"
            >
              ➤
            </button>
          </div>

        </div>
      )}

      {/* Botón flotante */}
      <button
        className="chat-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Cerrar chat" : "Abrir chat"}
      >
        {open ? "×" : "💬"}
      </button>
    </div>
  );
}
